import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import api, { formatApiError, setAccessToken, refreshAccessToken, csrfHeader } from "@/lib/api";
import type { User, UserRole, AuthResponse } from "@/types";

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  setUser: (u: User | false) => void;
  applyAuth: (data: AuthResponse) => User;
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const ROLE_LABELS: Record<UserRole, string> = {
  super_admin: "Super Admin",
  manager: "Manager",
  staff: "Staff",
  customer: "Customer",
};

export const STAFF_ROLES: UserRole[] = ["super_admin", "manager", "staff"];

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null | false>(null);
  const [loading, setLoading] = useState(true);

  const setUser = useCallback((u: User | false) => setUserState(u), []);

  const applyAuth = useCallback((data: AuthResponse): User => {
    setAccessToken(data.access_token);
    setUserState(data.user);
    return data.user;
  }, []);

  const bootstrap = useCallback(async () => {
    // Returning from OAuth handles its own exchange.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = await refreshAccessToken();
    if (!token) {
      setUserState(false);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get<User>("/auth/me");
      setUserState(data);
    } catch {
      setUserState(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = useCallback(
    async (email: string, password: string): Promise<User> => {
      const { data } = await api.post<AuthResponse>("/auth/login", { email, password });
      return applyAuth(data);
    },
    [applyAuth]
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout", {}, { headers: csrfHeader() });
    } catch {
      /* ignore */
    }
    setAccessToken(null);
    setUserState(false);
  }, []);

  const value: AuthContextValue = {
    user: user || null,
    isAuthenticated: !!user,
    loading,
    setUser,
    applyAuth,
    login,
    logout,
    refresh: bootstrap,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export { formatApiError };
